import { Box, Button, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';

import { IDiagram } from 'store/ticket-service/types';

import { NamespacesList } from 'core/enums/common';

import { ReactComponent as Maximize } from 'assets/icons/maximize.svg';
import { regularTextColor } from 'assets/styles/_palette';

interface IDiagramImageProps {
	diagram: IDiagram;
	onFullView?: (diagram: IDiagram) => void;
	onLoadError?: (diagram: IDiagram) => Promise<void>;
}

export const DiagramImage: FC<IDiagramImageProps> = ({ diagram, onFullView, onLoadError }) => {
	const { t: useCasesTranslate, i18n } = useTranslation(NamespacesList['useCasesStep']);

	const [isDiagramLoading, setIsDiagramLoading] = useState<boolean>(false);
	const [diagramHasError, setDiagramHasError] = useState<boolean>(false);

	useEffect(() => {
		if (diagram?.pictureLink) {
			fetch(diagram?.pictureLink || '')
				.then((response) => {
					if (response.status === 200) {
						setDiagramHasError(false);
					}

					if (response.status === 400) {
						setDiagramHasError(true);
					}
				})
				.catch((e) => {
					console.log('failed to fetch UML diagram: ', e);
					setDiagramHasError(true);
				});
		}
	}, [diagram]);

	const handleOnError = () => {
		setIsDiagramLoading(true);

		if (onLoadError) {
			onLoadError(diagram).then(() => {
				setIsDiagramLoading(false);
			});
		}
	};

	const handleFullViewClick = () => {
		if (onFullView) onFullView(diagram);
	};

	return (
		<Box>
			{isDiagramLoading ? (
				<Box
					sx={{
						width: '100%',
						height: '400px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Oval
						height={24}
						width={24}
						color="#fff"
						wrapperStyle={{}}
						wrapperClass=""
						visible={true}
						ariaLabel="oval-loading"
						secondaryColor="#4786ff"
						strokeWidth={5}
						strokeWidthSecondary={5}
					/>
				</Box>
			) : (
				<>
					{diagramHasError ? (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								minHeight: '300px',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
							}}
						>
							<Box
								sx={{
									flex: 1,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '0 32px',
								}}
							>
								<Typography
									sx={{
										color: regularTextColor,
										fontWeight: '700',
										fontSize: '1.2em',
										textAlign: 'center',
									}}
								>
									{useCasesTranslate('generalText.generateError')}
								</Typography>
							</Box>
							{onLoadError ? (
								<Button
									className="not-rtl-able"
									sx={{
										height: '44px',
										width: '240px',
										display: 'flex',
										gap: '8px',
										textTransform: 'none',
										fontWeight: '600',
										margin: '16px auto 0',
									}}
									variant="contained"
									color="primary"
									onClick={handleOnError}
								>
									{useCasesTranslate('buttons.regenerateDiagram')}
								</Button>
							) : null}
						</Box>
					) : (
						<>
							<img
								style={{
									maxWidth: '100%',
								}}
								alt="test"
								src={diagram?.pictureLink || ''}
							/>
							{onFullView ? (
								<Button
									sx={{
										position: 'absolute',
										top: { xs: '-25px', sm: '0' },
										right: {
											xs: i18n?.language === 'ar' ? 'unset' : '4px',
											sm: i18n?.language === 'ar' ? 'unset' : '12px',
										},
										left: {
											xs: i18n?.language === 'ar' ? '4px' : 'unset',
											sm: i18n?.language === 'ar' ? '12px' : 'unset',
										},
										minWidth: '24px',
										height: '24px',
										padding: '0',
									}}
									onClick={handleFullViewClick}
								>
									<Maximize />
								</Button>
							) : null}
						</>
					)}
				</>
			)}
		</Box>
	);
};
